import type { ReactNode } from 'react'

import { numberToCurrency as money } from '@/utils'
import { notCountedSentence } from '../AllAccountsCard/moneyText'
import {
  type HiddenCopyCounts,
  moneyMovementCount,
} from '../model/activityView'
import { monthLabel } from '../model/months'
import type { NotCounted } from '../model/portfolioOverview'
import type { ActivityTotals, DateWindow, MoneyFigure } from '../model/types'
import {
  countOf,
  dateRangeText,
  joinWithAnd,
  otherActivitySentence,
} from '../portfolioText'

interface ActivityTotalsStripProps {
  totals: ActivityTotals
  // The first and last date of the rows summed.
  window: DateWindow | null
  // With more than one account in view, the strip names how many it adds up.
  accountCount: number | null
  hiddenCopies: HiddenCopyCounts
  // Months with no statement inside those dates, said at the end like the money column above.
  notCounted: NotCounted[]
}

interface StripFigure {
  label: string
  value: ReactNode
  detail?: string
}

const NONE = <span className="text-ink-subtle">None found</span>

const amountOrNone = (figure: MoneyFigure) =>
  figure.count === 0 ? NONE : money(figure.amount)

const plural = (parts: [number, string, string][]) =>
  joinWithAnd(
    parts
      .filter(([count]) => count > 0)
      .map(([count, singular, many]) => countOf(count, singular, many)),
  )

const withCommissions = (detail: string, commissions: number) =>
  commissions > 0 ? `${detail}, ${money(commissions)} commissions` : detail

// Cash ledger rows the enricher never sorted. Shown only when there are some, never as deposits.
const unsortedFigures = ({
  unsortedIn,
  unsortedOut,
}: ActivityTotals): StripFigure[] =>
  unsortedIn.count + unsortedOut.count === 0
    ? []
    : [
        {
          label: 'Unsorted money in',
          value: amountOrNone(unsortedIn),
          detail: plural([[unsortedIn.count, 'ledger row', 'ledger rows']]),
        },
        {
          label: 'Unsorted money out',
          value: amountOrNone(unsortedOut),
          detail: plural([[unsortedOut.count, 'ledger row', 'ledger rows']]),
        },
      ]

const moneyFigures = (totals: ActivityTotals): StripFigure[] => [
  {
    label: 'Deposits',
    value: amountOrNone(totals.deposits),
    detail: plural([
      [totals.deposits.depositCount, 'deposit', 'deposits'],
      [totals.deposits.contributionCount, 'contribution', 'contributions'],
    ]),
  },
  {
    label: 'Withdrawals',
    value: amountOrNone(totals.withdrawals),
    detail: plural([
      [totals.withdrawals.withdrawalCount, 'withdrawal', 'withdrawals'],
      [totals.withdrawals.distributionCount, 'distribution', 'distributions'],
    ]),
  },
  {
    label: 'Transfers in',
    value: amountOrNone(totals.transfersIn),
    detail: plural([[totals.transfersIn.count, 'transfer', 'transfers']]),
  },
  {
    label: 'Transfers out',
    value: amountOrNone(totals.transfersOut),
    detail: plural([[totals.transfersOut.count, 'transfer', 'transfers']]),
  },
  ...unsortedFigures(totals),
  { label: 'Net money in and out', value: money(totals.netMoney) },
]

const tradeAndIncomeFigures = (
  totals: ActivityTotals,
  withFees: boolean,
): StripFigure[] => [
  {
    label: 'Bought',
    value: amountOrNone(totals.bought),
    detail: withCommissions(
      plural([[totals.bought.count, 'buy', 'buys']]),
      totals.bought.commissions,
    ),
  },
  {
    label: 'Sold',
    value: amountOrNone(totals.sold),
    detail: withCommissions(
      plural([[totals.sold.count, 'sell', 'sells']]),
      totals.sold.commissions,
    ),
  },
  {
    label: 'Dividends',
    value: amountOrNone(totals.dividends),
    detail: plural([[totals.dividends.count, 'payment', 'payments']]),
  },
  {
    label: 'Interest',
    value: amountOrNone(totals.interest),
    detail:
      totals.interest.charged < 0
        ? `${money(totals.interest.earned)} earned, ${money(totals.interest.charged)} margin interest`
        : plural([[totals.interest.count, 'payment', 'payments']]),
  },
  ...(withFees
    ? [
        {
          label: 'Fees',
          value: amountOrNone(totals.fees),
          detail: plural([[totals.fees.count, 'fee row', 'fee rows']]),
        },
      ]
    : []),
]

const leftOutPhrase = (totals: ActivityTotals, accountCount: number | null) => {
  const { sweeps, unsettledTrades, pairedTransfers, reinvestments } =
    totals.leftOut
  const parts = [
    sweeps > 0 ? 'sweeps' : '',
    unsettledTrades > 0
      ? countOf(unsettledTrades, 'unsettled trade', 'unsettled trades')
      : '',
    reinvestments > 0
      ? countOf(reinvestments, 'reinvestment', 'reinvestments')
      : '',
    pairedTransfers > 0
      ? `${countOf(pairedTransfers, 'paired transfer', 'paired transfers')} between ${accountCount == null ? 'accounts' : 'these accounts'}`
      : '',
  ].filter(Boolean)
  return parts.length === 0 ? '' : ` Left out: ${joinWithAnd(parts)}.`
}

export const hiddenCopiesSentence = ({
  ledgerRows,
  securitiesRows,
  secondStatementRows,
}: HiddenCopyCounts): string => {
  const parts = [
    ledgerRows > 0
      ? `${countOf(ledgerRows, 'cash-ledger row')} hidden as ${ledgerRows === 1 ? 'the cash leg of a trade or other securities activity' : 'cash legs of trades and other securities activity'}`
      : '',
    securitiesRows > 0
      ? `${countOf(securitiesRows, 'securities row')} hidden as ${securitiesRows === 1 ? 'a copy of a cash-ledger row' : 'copies of cash-ledger rows'}`
      : '',
    ...secondStatementRows.map(
      ({ month, count }) =>
        `${countOf(count, 'row')} from a second statement for ${monthLabel(month)} hidden as ${count === 1 ? 'a copy' : 'copies'} of the first`,
    ),
  ].filter(Boolean)
  return parts.length === 0 ? '' : ` ${joinWithAnd(parts)}.`
}

const leadSentence = ({
  totals,
  window,
  accountCount,
  hiddenCopies,
  notCounted,
}: ActivityTotalsStripProps) => {
  const dates = window ? `Totals for ${dateRangeText(window)}` : 'Totals'
  const scope = accountCount == null ? '' : ` in all ${accountCount} accounts`
  const notCountedText = notCountedSentence(notCounted)
  const ending = notCountedText ? ` ${notCountedText}` : ''
  return `${dates}${scope}, from the activity and the cash ledger.${leftOutPhrase(totals, accountCount)}${hiddenCopiesSentence(hiddenCopies)}${ending}`
}

const hasMoneyMovement = (totals: ActivityTotals) =>
  moneyMovementCount(totals) + totals.fees.count > 0

const noMoneySentence = ({ other }: ActivityTotals) =>
  other.count > 0
    ? otherActivitySentence(other.count)
    : 'No deposits, withdrawals, transfers or fees found in either.'

const FigureRow = ({ figures }: { figures: StripFigure[] }) => (
  <dl className="grid grid-cols-2 gap-6 md:grid-cols-5">
    {figures.map((figure) => (
      <div key={figure.label} className="min-w-0">
        <dt className="text-xs text-ink-subtle">{figure.label}</dt>
        <dd className="mt-0.5 tabular-nums">{figure.value}</dd>
        {figure.detail && (
          <dd className="mt-0.5 text-xs text-ink-subtle">{figure.detail}</dd>
        )}
      </div>
    ))}
  </dl>
)

// Money in and out, trades, income and fees for exactly the rows the list shows.
export const ActivityTotalsStrip = (props: ActivityTotalsStripProps) => {
  const { totals } = props
  const movedMoney = hasMoneyMovement(totals)
  return (
    <div className="flex flex-col gap-5">
      <p className="text-xs text-ink-subtle">{leadSentence(props)}</p>
      {movedMoney ? (
        <FigureRow figures={moneyFigures(totals)} />
      ) : (
        <p className="text-sm">{noMoneySentence(totals)}</p>
      )}
      <FigureRow figures={tradeAndIncomeFigures(totals, movedMoney)} />
    </div>
  )
}
